import React, { useEffect, useState } from 'react';
import { businessesByMunicipality, businessesByCategories, businessesByType } from '../../../store/actions/actions';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FileDownloadOutlined } from '@mui/icons-material';
import { Button, Grid, CircularProgress } from '@mui/material';
import { toastNotification } from '../../../utils/toastNotification';
import APIClient from '../../../services/ApiClient';
import { saveAs } from 'file-saver';
import MainCard from '../../../utils/MainCard';
import { BusinessFilter } from './Filters/Business';
import SubCard from '../../../utils/SubCard';
import { StatsChart } from './Stats';

export const BusinessesReports = ({ startDate, endDate }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const businessesByMunicipalityResponse = useSelector(({ reports }) => reports.businessesByMunicipality);
  const businessesByTypeResponse = useSelector(({ reports }) => reports.businessesByType);
  const businessesByCategoriesResponse = useSelector(({ reports }) => reports.businessesByCategories);

  const [loadingButton, setLoadingButton] = useState(false);
  const [categoryChips, setCategoryChips] = useState([]);
  const [municipalityChips, setMunicipalityChips] = useState([]);
  const [entityTypeChips, setEntityTypeChips] = useState([]);

  useEffect(() => {
    if (startDate || endDate) {
      let payload = {
        startDate: startDate ? startDate : undefined,
        endDate: endDate ? endDate : undefined,
      };
      if (entityTypeChips?.length) payload['entityTypes'] = entityTypeChips.map((en) => en._id);
      if (categoryChips?.length) payload['categories'] = categoryChips.map((cat) => cat._id);
      if (municipalityChips?.length) payload['municipalities'] = municipalityChips.map((mun) => mun._id);
      dispatch(businessesByMunicipality(payload));
      dispatch(businessesByType(payload));
      dispatch(businessesByCategories(payload));
    }
  }, [municipalityChips, entityTypeChips, categoryChips]);

  const onDocumentDownload = async (event) => {
    setLoadingButton(true);
    event.preventDefault();
    let payload = {};
    if (endDate) payload['endDate'] = endDate;
    if (startDate) payload['startDate'] = startDate;
    if (entityTypeChips?.length) payload['entityTypes'] = entityTypeChips.map((en) => en._id);
    if (categoryChips?.length) payload['categories'] = categoryChips.map((cat) => cat._id);
    if (municipalityChips?.length) payload['municipalities'] = municipalityChips.map((mun) => mun._id);
    await APIClient.post(`/reports/businesses/export`, payload, {
      responseType: 'blob',
    })
      .then((res) => {
        setLoadingButton(false);
        if (res.data) {
          if (res.headers['content-type'] == 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            saveAs(res.data, 'Organisations_Stats.csv');
          }
        } else {
          return toastNotification('error', 'Something went wrong!');
        }
      })
      .catch((err) => {
        setLoadingButton(false);
        return toastNotification('error', 'Something went wrong!');
      });
  };

  if (
    businessesByMunicipalityResponse.loading ||
    businessesByTypeResponse.loading ||
    businessesByCategoriesResponse.loading
  ) {
    return <div class="Stats-loader">Loading...</div>;
  }
  return (
    <>
      <MainCard title={t('generalStatistics')} sx={{ color: '#01486B', fontFamily: 'Mark-Bold !important' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <BusinessFilter
                  categoryChips={categoryChips}
                  entityTypeChips={entityTypeChips}
                  municipalityChips={municipalityChips}
                  setCategoryChips={setCategoryChips}
                  setEntityTypeChips={setEntityTypeChips}
                  setMunicipalityChips={setMunicipalityChips}
                />
              </Grid>
              <Grid item xs={12}>
                <SubCard
                  title={t('charts')}
                  sx={{ color: '#01486B', fontFamily: 'Mark-Bold !important' }}
                  secondary={
                    <Button
                      variant="contained"
                      disabled={loadingButton === true ? true : false}
                      sx={{
                        backgroundColor: '#01486B !important',
                        '&:hover': { backgroundColor: '#012b40 !important' },
                        width: '160px',
                        fontFamily: 'Mark-Regular',
                        fontSize: '14px',
                        textTransform: 'none !important',
                      }}
                      onClick={(e) => {
                        onDocumentDownload(e);
                      }}
                    >
                      {loadingButton ? (
                        <>
                          <CircularProgress size={24} style={{ color: '#fff' }} />
                        </>
                      ) : (
                        <>
                          <FileDownloadOutlined /> {t('exportExcel')}
                        </>
                      )}
                    </Button>
                  }
                >
                  <Grid container spacing={3}>
                    <StatsChart
                      response={businessesByMunicipalityResponse}
                      label={t('businessesByMunicipality')}
                      data={
                        businessesByMunicipalityResponse?.data?.businesses
                          ? businessesByMunicipalityResponse.data.businesses
                          : []
                      }
                      chart={'Bar'}
                    />
                    <StatsChart
                      response={businessesByTypeResponse}
                      label={t('businessesByType')}
                      data={businessesByTypeResponse?.data?.businesses ? businessesByTypeResponse.data.businesses : []}
                      chart={'Bar'}
                    />
                    <StatsChart
                      response={businessesByCategoriesResponse}
                      label={t('businessesByCategories')}
                      data={
                        businessesByCategoriesResponse?.data?.businesses
                          ? businessesByCategoriesResponse.data.businesses
                          : []
                      }
                      chart={'Bar'}
                    />
                  </Grid>
                </SubCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainCard>
    </>
  );
};
